// Copyright 2022, Imprivata, Inc.  All rights reserved.
import { Flex, Spin } from "antd";
import "./CameraCard.less";
import { LoadingOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { LoadingSpinner } from "../../components/loading/Loading";
import { isMobileDeviceDetected } from "../../utils/MobileDeviceHelper";

interface CameraCardProps {
  capturing: boolean;
  captured: boolean;
  video: React.MutableRefObject<HTMLVideoElement | null>;
  canvas: React.MutableRefObject<HTMLCanvasElement | null>;
  cameraError: boolean;
  isInitializing?: boolean;
  onVideoPlay?: () => void;
  onVideoElementReady?: () => void;
}
const CameraCard: React.FC<CameraCardProps> = ({
  capturing,
  captured,
  video,
  canvas,
  cameraError,
  isInitializing = false,
  onVideoPlay,
  onVideoElementReady,
}) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  // Notify parent when video element ref is set
  useEffect(() => {
    if (video.current && onVideoElementReady) {
      onVideoElementReady();
    }
  }, [video, onVideoElementReady]);
  // Derived state for cleaner conditional logic
  const showVideo = !captured && !cameraError && isVideoPlaying;
  const _showFaceGuide = showVideo && !capturing;
  const _showStatusIndicators = !showVideo || capturing;
  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
    onVideoPlay?.();
  };
  const isMobile = isMobileDeviceDetected();
  const videoElement = useMemo(
    () => (
      <Flex vertical align="center" className="w-100">
        <div
          className={
            isMobile
              ? "aspect-ratio-container-mobile"
              : "aspect-ratio-container-desktop"
          }
        >
          <video
            className="camera-video"
            ref={video}
            muted
            autoPlay
            playsInline
            tabIndex={-1}
            data-cy="photo-video-stream"
            onPlay={handleVideoPlay}
          />
        </div>
      </Flex>
    ),
    [video, isMobile]
  );
  return (
    <Flex vertical align="center" justify="center" className="camera-card" data-testid="camera-card">
      <div className="container">
        <div
          data-cy="photo-video-stream-container"
          className={`${capturing ? "mask" : ""}`}
          style={{
            display: captured || cameraError || !isVideoPlaying ? "none" : "",
          }}
        >
          {videoElement}
        </div>
        <canvas className={"still hidden"} ref={canvas} />
        {/* Status indicators container - exclude capturing spinner */}
        <div
          style={{
            display:
              (isInitializing || captured || !isVideoPlaying) &&
              !cameraError &&
              !capturing
                ? ""
                : "none",
          }}
        >
          <Flex vertical align="center">
            <LoadingSpinner />
          </Flex>
        </div>
        {/* Capturing spinner - positioned absolutely outside normal flow */}
        {capturing && (
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 80 }} spin />}
            className="svgs-animation"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    </Flex>
  );
};
export default CameraCard;
